import React, { useState } from "react";
import { LinkIcon, PhotoIcon, PlayIcon, RemoveIcon, VimeoIcon, YoutubeIcon } from "./InlineIcons";
import { ImageAttachment, LinkAttachment } from "./types";

const hideOnError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.style.display = "none";
};

interface AttachmentCardProps {
  image?: ImageAttachment;
  link?: LinkAttachment;
  // Omitted on read-only renders (e.g. inside a posted card).
  onRemove?: () => void;
}

const RemoveButton = ({ onRemove }: { onRemove: () => void }) => (
  <button
    className="community-composer__attachment-remove"
    type="button"
    aria-label="Remove attachment"
    onClick={onRemove}
  >
    <RemoveIcon />
  </button>
);

const AttachmentCard = ({ image, link, onRemove }: AttachmentCardProps) => {
  const [videoThumbFailed, setVideoThumbFailed] = useState(false);
  const [linkThumbFailed, setLinkThumbFailed] = useState(false);

  if (image) {
    return (
      <div className="community-composer__attachment community-composer__attachment--image">
        {onRemove && <RemoveButton onRemove={onRemove} />}
        <span className="community-composer__attachment-chip community-composer__attachment-chip--image">
          <PhotoIcon />
          Image
        </span>
        <img
          className="community-composer__attachment-photo"
          src={image.src}
          alt={image.alt}
        />
      </div>
    );
  }

  if (!link) return null;

  const favicon =
    link.favicon ||
    `https://www.google.com/s2/favicons?domain=${encodeURIComponent(link.domain)}&sz=32`;

  // Eager fetch is still in flight. Show a skeleton — the real hostname
  // (so the user can see which link is loading), no fabricated title or
  // description anywhere.
  if (link.status === "loading") {
    return (
      <div className="community-composer__attachment community-composer__attachment--link community-composer__attachment--loading">
        {onRemove && <RemoveButton onRemove={onRemove} />}
        <div className="community-composer__attachment-link-body">
          <div className="community-composer__attachment-domain">
            <span>{link.domain}</span>
          </div>
          <div className="community-composer__attachment-skeleton-line community-composer__attachment-skeleton-line--title" />
          <div className="community-composer__attachment-skeleton-line community-composer__attachment-skeleton-line--desc" />
        </div>
      </div>
    );
  }

  // YouTube/Vimeo are known the instant the URL is classified (see
  // classifyUrl), well before any unfurl completes — so this never
  // degrades to the generic bare-chip below. A resolved oEmbed thumbnail
  // renders in the 16:9 frame; if there isn't one (bare, or the image
  // failed to load), a plum gradient placeholder stands in — the video-ness
  // stays honest either way.
  if (link.kind === "youtube" || link.kind === "vimeo") {
    const isYoutube = link.kind === "youtube";
    const label = isYoutube ? "YouTube" : "Vimeo";
    const showThumb = Boolean(link.image) && !videoThumbFailed;

    return (
      <div className="community-composer__attachment community-composer__attachment--video">
        {onRemove && <RemoveButton onRemove={onRemove} />}
        <a
          className="community-composer__video-thumb"
          href={link.url}
          target="_blank"
          rel="noopener nofollow"
          aria-label={`Play ${label} video in a new tab`}
        >
          {showThumb ? (
            <img
              src={link.image}
              alt=""
              loading="lazy"
              onError={() => setVideoThumbFailed(true)}
            />
          ) : (
            <div className="community-composer__video-fallback">
              <PlayIcon />
            </div>
          )}
          <span className="community-composer__play" aria-hidden="true">
            <PlayIcon />
          </span>
          <span
            className={`community-composer__attachment-chip community-composer__attachment-chip--${link.kind}`}
          >
            {isYoutube ? <YoutubeIcon /> : <VimeoIcon />}
            {label}
          </span>
        </a>
        <div className="community-composer__video-meta">
          <div className="community-composer__video-title">
            {link.title || `${label} video`}
          </div>
          <div className="community-composer__video-byline">{link.domain}</div>
        </div>
      </div>
    );
  }

  // Nothing usable was scraped (unfurl failed or timed out). Show a tidy
  // bare chip — favicon, readable site name, truncated URL — rather than
  // either a broken-looking card or hiding the link entirely.
  if (link.status === "bare") {
    const shortUrl = link.url.replace(/^https?:\/\/(www\.)?/i, "");

    return (
      <div className="community-composer__attachment">
        {onRemove && <RemoveButton onRemove={onRemove} />}
        <a
          className="community-composer__attachment-link community-composer__attachment-link--bare"
          href={link.url}
          target="_blank"
          rel="noopener nofollow"
        >
          <img src={favicon} alt="" width={16} height={16} onError={hideOnError} />
          <LinkIcon />
          {link.site && (
            <span className="community-composer__attachment-link-site">{link.site}</span>
          )}
          <span className="community-composer__attachment-link-url">{shortUrl}</span>
        </a>
      </div>
    );
  }

  return (
    <div className="community-composer__attachment community-composer__attachment--link">
      {onRemove && <RemoveButton onRemove={onRemove} />}
      <a
        className="community-composer__attachment-link"
        href={link.url}
        target="_blank"
        rel="noopener nofollow"
      >
        {link.image && !linkThumbFailed && (
          <div className="community-composer__attachment-link-thumb" aria-hidden="true">
            <img
              src={link.image}
              alt=""
              loading="lazy"
              onError={() => setLinkThumbFailed(true)}
            />
          </div>
        )}
        <div className="community-composer__attachment-link-body">
          <div className="community-composer__attachment-domain">
            <img src={favicon} alt="" width={14} height={14} onError={hideOnError} />
            <span>{link.domain}</span>
          </div>
          {link.title && (
            <div className="community-composer__attachment-title">{link.title}</div>
          )}
          {link.description && (
            <div className="community-composer__attachment-desc">{link.description}</div>
          )}
        </div>
      </a>
    </div>
  );
};

export default AttachmentCard;
